import { useTradingPositionListPositions } from '@/lib/endpoints';
import { PlaeePositionResponseDtoStatus, TradingPositionListPositionsStatus } from '@/lib/models';
import type { PlaeePositionResponseDto } from '@/lib/models';
import { fmtPrice, signedUsd } from '@/lib/trading/format';
import { CollapsibleSection } from '@/components/features/BinaryMarkets/CollapsibleSection';
import { SNAKE_SECTION_ROW_DIVIDED, SNAKE_SECTION_HEADER } from './section-row-style';

export interface SnakeMyHistoryProps {
  /** The window currently being viewed — history is scoped to this outcome. */
  outcomeId: string;
}

/**
 * My History (ARC-84 §5): the customer's finished positions **for the window on
 * screen** — SETTLED by resolution (Won/Lost) or CLOSED because they sold out
 * early (Sold), with realized P&L. Scoped to this window's `outcomeId`: each snake
 * window is its own market/outcome, so moving to another window shows that
 * window's history. A finished position is a position transition (not a trade), so
 * this reads the positions mirror. Live via {@link useSettlementResync}.
 */
const FINISHED = `${TradingPositionListPositionsStatus.closed},${TradingPositionListPositionsStatus.settled}`;

export function SnakeMyHistory({ outcomeId }: SnakeMyHistoryProps) {
  const { data: positions = [] } = useTradingPositionListPositions(
    { outcomeId, statuses: FINISHED },
    { query: { enabled: Boolean(outcomeId) } },
  );
  const finished = positions.filter(
    (p) => p.status === PlaeePositionResponseDtoStatus.SETTLED || p.status === PlaeePositionResponseDtoStatus.CLOSED,
  );
  const realized = finished.reduce((sum, p) => sum + p.realizedPnl, 0);

  return (
    <CollapsibleSection
      title="My History"
      className={SNAKE_SECTION_ROW_DIVIDED}
      headerClassName={SNAKE_SECTION_HEADER}
      disabled={finished.length === 0}
      summary={
        finished.length > 0 ? (
          <span
            className={`rounded-[51px] px-2.5 py-0.5 text-[14px] font-medium tracking-[-0.28px] ${
              realized >= 0 ? 'bg-up/10 text-up' : 'bg-danger/10 text-danger-alt'
            }`}
          >
            {signedUsd(realized)}
          </span>
        ) : undefined
      }
    >
      {finished.length === 0 ? (
        <p className="text-sm text-muted">No finished positions yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {finished.map((p) => (
            <HistoryRow key={p.positionId} position={p} />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}

/** One finished-position row: market + entry price × qty, realized P&L, and result. */
function HistoryRow({ position }: { position: PlaeePositionResponseDto }) {
  const gain = position.realizedPnl >= 0;
  const tone = gain ? 'text-up' : 'text-danger-alt';
  // CLOSED = exited early ("Sold"); SETTLED = resolved by the market (Won/Lost).
  const result = position.status === PlaeePositionResponseDtoStatus.CLOSED ? 'Sold' : gain ? 'Won' : 'Lost';

  return (
    <div className="flex items-center justify-between gap-3 rounded-[12px] border border-border bg-surface px-3 py-2">
      <div className="flex flex-col">
        <span className="text-[15px] font-semibold text-primary">
          {position.marketName ?? position.outcomeName ?? 'Market'}
        </span>
        <span className="text-[12px] text-muted">
          Entry {fmtPrice(position.avgEntryPrice ?? 0)} · ×{position.quantity}
        </span>
      </div>

      <div className="flex flex-col items-end">
        <span className={`text-[14px] font-semibold tabular-nums ${tone}`}>{signedUsd(position.realizedPnl)}</span>
        <span className={`text-[12px] font-medium ${tone}`}>{result}</span>
      </div>
    </div>
  );
}
